import React, { useCallback, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import CheckInStatusPresets from '../components/CheckInStatusPresets';
import { Typography, Paper, CircularProgress, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel, Autocomplete } from '@mui/material';
import { Activity, Building2, Copy, Edit2, FileText, Plus, Trash2, Download, User } from 'lucide-react';
import {
  fetchExtCheckInBySubscriptionId,
  uploadExtCheckInBoardingPass,
  updateExtCheckInPnr,
  updateExtCheckInStatus,
  updateExtCheckInPassengers
} from '../services/api';
import { getStatusRadixStyles } from '../utils/statusUtils';
import { statusLabel, statusOptions, subStatusForStatus, subStatusLabel, subStatusOptions } from '../utils/webcheckinStatus';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(enLocale);

const SidebarSection = ({ title, icon: Icon, accentClass = 'bg-blue-50', children, action }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div className={`px-3 py-2.5 ${accentClass} border-b border-gray-100 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-white/80 flex items-center justify-center shadow-sm">
          <Icon className="w-3.5 h-3.5 text-gray-700" />
        </div>
        <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wider">{title}</h3>
      </div>
      {action}
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const SidebarCopyButton = ({ value, label, onCopy }) => {
  if (!value) return null;

  return (
    <button
      onClick={() => onCopy(value, label)}
      className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
      title={`Copy ${label}`}
    >
      <Copy className="w-3 h-3" />
    </button>
  );
};

const SidebarInfoRow = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 py-1.5">
    <span className="text-xs text-gray-500 font-medium">{label}</span>
    <div className="min-w-0 text-right">{children}</div>
  </div>
);

const SidebarStatusBadge = ({ status }) => {
  const styles = getStatusRadixStyles(status?.toLowerCase() || 'unknown');

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles.bgColor} ${styles.textColor} ${styles.borderColor}`}>
      {statusLabel(status)}
    </span>
  );
};

/**
 * External Partner Check-in Details Page
 * @returns {JSX.Element} ExtCheckInDetailsPage component
 */
const ExtCheckInDetailsPage = () => {
  const { subscriptionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInData, setCheckInData] = useState(null);
  const [activeTab, setActiveTab] = useState('flightDetails');
  const [editingPnr, setEditingPnr] = useState(false);
  const [pnrValue, setPnrValue] = useState('');
  const [pnrError, setPnrError] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [subStatusValue, setSubStatusValue] = useState('');
  const [statusMessageValue, setStatusMessageValue] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState('success');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [showPassengerDialog, setShowPassengerDialog] = useState(false);
  const [passengerFormData, setPassengerFormData] = useState({});
  const [deletingPassenger, setDeletingPassenger] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passengerFormErrors, setPassengerFormErrors] = useState({});
  const [expandedBoardingPass, setExpandedBoardingPass] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);

  // Initialize country options on mount
 useEffect(() => {
    const countryNames = countries.getNames('en'); // { AF: 'Afghanistan', AL: 'Albania', ... }

    const options = Object.entries(countryNames).map(([code, name]) => ({
      label: `${name} (${code})`,
      value: code
    }));

    setCountryOptions(options);
  }, []);

  const applyLoadedCheckInData = useCallback((data) => {
    setCheckInData(data);
    setPnrValue(data.pnr || '');
    setStatusValue((data.checkinStatus || '').toUpperCase());
    setSubStatusValue(data.checkinSubStatus || '');
    setStatusMessageValue(data.statusMessage || '');
  }, []);

  // Fetch check-in data on mount
  useEffect(() => {
    const loadCheckInData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchExtCheckInBySubscriptionId(subscriptionId);
        applyLoadedCheckInData(data);
      } catch (err) {
        setError(err.message || 'Failed to load check-in details');
        console.error('Error loading check-in data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (subscriptionId) {
      loadCheckInData();
    }
  }, [subscriptionId, applyLoadedCheckInData]);

  // Validation helper functions
  const validatePnr = (value) => {
    if (!value) return 'PNR is required';
    if (value.length < 6) return 'PNR must be at least 6 characters';
    if (value.length > 14) return 'PNR must not exceed 14 characters';
    return '';
  };

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validatePhone = (value) => {
    if (!value) return 'Phone is required';
    const phoneRegex = /^\+\d{10,15}$/;
    if (!phoneRegex.test(value)) return 'Phone must be in format +[country code][number] (10-15 digits)';
    return '';
  };

  // Show toast notification
  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const copyToClipboard = async (text, label) => {
    const copyValue = String(text || '').trim();
    if (!copyValue) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyValue);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = copyValue;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      showNotification(`${label} copied`);
    } catch (err) {
      showNotification(`Failed to copy ${label}`, 'error');
    }
  };

  // Handle PNR update
  const handlePnrUpdate = async () => {
    const error = validatePnr(pnrValue);
    if (error) {
      setPnrError(error);
      return;
    }

    try {
      setPnrError('');
      await updateExtCheckInPnr(subscriptionId, pnrValue);
      const refreshed = await fetchExtCheckInBySubscriptionId(subscriptionId);
      applyLoadedCheckInData(refreshed);
      setEditingPnr(false);
      showNotification('PNR updated successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to update PNR', 'error');
    }
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    try {
      console.log('Attempting to update status to:', statusValue);
      await updateExtCheckInStatus(subscriptionId, statusValue, subStatusValue, statusMessageValue);
      const refreshed = await fetchExtCheckInBySubscriptionId(subscriptionId);
      applyLoadedCheckInData(refreshed);
      setEditingStatus(false);
      showNotification('Check-in status updated successfully');
    } catch (err) {
      console.error('Status update error:', err);
      showNotification(err.message || 'Failed to update status', 'error');
    }
  };

  // Handle boarding pass upload
  const handleBoardingPassUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      await uploadExtCheckInBoardingPass(subscriptionId, file);
      // Reload data to get updated boarding pass URLs
      const data = await fetchExtCheckInBySubscriptionId(subscriptionId);
      applyLoadedCheckInData(data);
      showNotification('Boarding pass uploaded successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to upload boarding pass', 'error');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle passenger edit
  const handleEditPassenger = (passenger, index) => {
    setEditingPassenger(index);
    setPassengerFormData({ ...passenger });
    setShowPassengerDialog(true);
  };

  // Handle passenger save
  const handleSavePassenger = async () => {
    try {
      // Validate required fields
      const errors = {};

      if (!passengerFormData.first_name?.trim()) {
        errors.first_name = 'First name is required';
      }
      if (!passengerFormData.last_name?.trim()) {
        errors.last_name = 'Last name is required';
      }

      const emailError = validateEmail(passengerFormData.email || '');
      if (emailError) errors.email = emailError;

      const phoneError = validatePhone(passengerFormData.phone || '');
      if (phoneError) errors.phone = phoneError;

      if (Object.keys(errors).length > 0) {
        setPassengerFormErrors(errors);
        return;
      }

      // Clean up the passenger data - remove empty nested objects
      const cleanedPassenger = {
        first_name: passengerFormData.first_name || '',
        last_name: passengerFormData.last_name || '',
        email: passengerFormData.email || '',
        phone: passengerFormData.phone || '',
        date_of_birth: passengerFormData.date_of_birth || ''
      };

      // Add passport info only if at least one field is filled
      if (passengerFormData.passport?.number || passengerFormData.passport?.expiry_date || passengerFormData.passport?.nationality) {
        cleanedPassenger.passport = {
          number: passengerFormData.passport.number || '',
          expiry_date: passengerFormData.passport.expiry_date || '',
          nationality: passengerFormData.passport.nationality || ''
        };
      }

      // Add seat preference only if at least one field is filled
      if (passengerFormData.seat_preference?.row_preference || passengerFormData.seat_preference?.column_preference) {
        cleanedPassenger.seat_preference = {
          row_preference: passengerFormData.seat_preference.row_preference || '',
          column_preference: passengerFormData.seat_preference.column_preference || ''
        };
      }

      const updatedPassengers = [...(checkInData.passengers || [])];
      if (editingPassenger !== null) {
        updatedPassengers[editingPassenger] = cleanedPassenger;
      } else {
        updatedPassengers.push(cleanedPassenger);
      }

      await updateExtCheckInPassengers(subscriptionId, updatedPassengers);
      const refreshed = await fetchExtCheckInBySubscriptionId(subscriptionId);
      applyLoadedCheckInData(refreshed);
      setShowPassengerDialog(false);
      setEditingPassenger(null);
      setPassengerFormData({});
      setPassengerFormErrors({});
      showNotification(editingPassenger !== null ? 'Passenger updated successfully' : 'Passenger added successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to save passenger', 'error');
    }
  };

  // Handle passenger delete
  const handleDeletePassenger = async () => {
    try {
      const updatedPassengers = checkInData.passengers.filter((_, idx) => idx !== deletingPassenger);
      await updateExtCheckInPassengers(subscriptionId, updatedPassengers);
      const refreshed = await fetchExtCheckInBySubscriptionId(subscriptionId);
      applyLoadedCheckInData(refreshed);
      setShowDeleteConfirm(false);
      setDeletingPassenger(null);
      showNotification('Passenger deleted successfully');
    } catch (err) {
      showNotification(err.message || 'Failed to delete passenger', 'error');
    }
  };

  if (loading) {
    return (
      <MainLayout activeTab="extPartnerCheckin" setActiveTab={() => {}}>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout activeTab="extPartnerCheckin" setActiveTab={() => {}}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}>
          <Paper sx={{ p: 3, backgroundColor: '#fee2e2' }}>
            <Typography variant="h6" color="error">Error</Typography>
            <Typography variant="body2" color="error">{error}</Typography>
          </Paper>
        </div>
      </MainLayout>
    );
  }

  if (!checkInData) {
    return (
      <MainLayout activeTab="extPartnerCheckin" setActiveTab={() => {}}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6">No data found</Typography>
          </Paper>
        </div>
      </MainLayout>
    );
  }

  const statusMessageText = (checkInData.statusMessage || '').trim();
  const hasDistinctStatusMessage = statusMessageText && statusMessageText !== checkInData.checkinSubStatus;
  const primaryPassenger = Array.isArray(checkInData.passengers) ? checkInData.passengers[0] : null;
  const travellerLastName = (primaryPassenger?.last_name || '').trim();
  const travellerPhone = (primaryPassenger?.phone || '').trim();
  const travellerEmail = (primaryPassenger?.email || '').trim();

  return (
    <MainLayout activeTab="extPartnerCheckin" setActiveTab={() => {}}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', padding: '16px' }}>
        {/* Header */}
        <div style={{ marginBottom: '16px' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            External Partner Check-in Details
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Subscription ID: {subscriptionId}
          </Typography>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', gap: '16px', minHeight: 0 }}>
          {/* Left Content Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '16px' }}>
              {['flightDetails', 'boardingPass', 'passengers'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: activeTab === tab ? '2px solid #3b82f6' : 'none',
                    color: activeTab === tab ? '#3b82f6' : '#6b7280',
                    fontWeight: activeTab === tab ? '600' : '500',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    fontSize: '14px'
                  }}
                >
                  {tab === 'flightDetails' && 'Flight Details'}
                  {tab === 'boardingPass' && 'Boarding Pass'}
                  {tab === 'passengers' && 'Passengers'}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {activeTab === 'flightDetails' && (
                <Paper sx={{ p: 3 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <Typography variant="caption" color="textSecondary">Airline</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>{checkInData.airline || 'N/A'}</Typography>
                    </div>
                    <div>
                      <Typography variant="caption" color="textSecondary">Flight Number</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>{checkInData.flightNumber || 'N/A'}</Typography>
                    </div>
                    <div>
                      <Typography variant="caption" color="textSecondary">Origin</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>{checkInData.origin || 'N/A'}</Typography>
                    </div>
                    <div>
                      <Typography variant="caption" color="textSecondary">Destination</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>{checkInData.destination || 'N/A'}</Typography>
                    </div>
                    <div>
                      <Typography variant="caption" color="textSecondary">Departure Date</Typography>
                      <Typography variant="body2" sx={{ fontWeight: '600' }}>{checkInData.departureDate || 'N/A'}</Typography>
                    </div>
                  </div>
                </Paper>
              )}

              {activeTab === 'boardingPass' && (
                <Paper sx={{ p: 3 }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Button
                      variant="contained"
                      component="label"
                      disabled={uploadingFile}
                      sx={{ mb: 2 }}
                    >
                      {uploadingFile ? 'Uploading...' : 'Upload Boarding Pass'}
                      <input
                        type="file"
                        accept=".pdf"
                        hidden
                        onChange={handleBoardingPassUpload}
                      />
                    </Button>
                  </div>

                  {checkInData.boardingPassUrls && checkInData.boardingPassUrls.length > 0 ? (
                    <div>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: '600' }}>
                        Boarding Passes ({checkInData.boardingPassUrls.length})
                      </Typography>
                      <div style={{ display: 'grid', gap: '16px' }}>
                        {checkInData.boardingPassUrls.map((url, idx) => (
                          <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                            {/* Header with title and download button */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flex: 1 }} onClick={() => setExpandedBoardingPass(expandedBoardingPass === idx ? null : idx)}>
                                <Typography variant="body2" sx={{ fontWeight: '600' }}>{`Boarding Pass ${idx + 1}`}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {expandedBoardingPass === idx ? '(Click to collapse)' : '(Click to preview)'}
                                </Typography>
                              </div>
                              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                                <Download size={18} />
                              </a>
                            </div>

                            {/* PDF Preview */}
                            {expandedBoardingPass === idx && (
                              <div style={{ padding: '12px', backgroundColor: '#ffffff' }}>
                                <iframe
                                  src={`${url}#toolbar=0`}
                                  style={{
                                    width: '100%',
                                    height: '600px',
                                    border: 'none',
                                    borderRadius: '4px'
                                  }}
                                  title={`Boarding Pass ${idx + 1}`}
                                  onError={() => (
                                    <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#fef2f2', borderRadius: '4px' }}>
                                      <Typography variant="body2" color="error">
                                        Unable to preview PDF. Please download to view.
                                      </Typography>
                                    </div>
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No boarding passes uploaded yet</Typography>
                  )}
                </Paper>
              )}

              {activeTab === 'passengers' && (
                <Paper sx={{ p: 3 }}>
                  <div style={{ marginBottom: '16px' }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setEditingPassenger(null);
                        setPassengerFormData({});
                        setShowPassengerDialog(true);
                      }}
                    >
                      <Plus size={18} style={{ marginRight: '8px' }} />
                      Add Passenger
                    </Button>
                  </div>

                  {checkInData.passengers && checkInData.passengers.length > 0 ? (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {checkInData.passengers.map((passenger, idx) => (
                        <div key={idx} style={{ padding: '12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                            <Typography variant="body2" sx={{ fontWeight: '600' }}>
                              {passenger.first_name} {passenger.last_name}
                            </Typography>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button onClick={() => handleEditPassenger(passenger, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}>
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => { setDeletingPassenger(idx); setShowDeleteConfirm(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Basic Info */}
                          <Typography variant="caption" color="textSecondary">Email: {passenger.email}</Typography>
                          <Typography variant="caption" color="textSecondary" display="block">Phone: {passenger.phone}</Typography>
                          {passenger.date_of_birth && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Date of Birth: {passenger.date_of_birth}
                            </Typography>
                          )}

                          {/* Passport Info */}
                          {passenger.passport && (passenger.passport.number || passenger.passport.expiry_date || passenger.passport.nationality) && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                              <Typography variant="caption" sx={{ fontWeight: '600', display: 'block', mb: 0.5 }}>Passport:</Typography>
                              {passenger.passport.number && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Number: {passenger.passport.number}
                                </Typography>
                              )}
                              {passenger.passport.expiry_date && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Expiry: {passenger.passport.expiry_date}
                                </Typography>
                              )}
                              {passenger.passport.nationality && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Nationality: {passenger.passport.nationality}
                                </Typography>
                              )}
                            </div>
                          )}

                          {/* Seat Preference */}
                          {passenger.seat_preference && (passenger.seat_preference.row_preference || passenger.seat_preference.column_preference) && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                              <Typography variant="caption" sx={{ fontWeight: '600', display: 'block', mb: 0.5 }}>Seat Preference:</Typography>
                              {passenger.seat_preference.row_preference && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Row: {passenger.seat_preference.row_preference}
                                </Typography>
                              )}
                              {passenger.seat_preference.column_preference && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Column: {passenger.seat_preference.column_preference}
                                </Typography>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No passengers added yet</Typography>
                  )}
                </Paper>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-96 flex-shrink-0 space-y-2 overflow-y-auto pr-1">
            <SidebarSection
              title="Check-in Status"
              icon={Activity}
              accentClass="bg-blue-50"
              action={!editingStatus && (
                <button
                  onClick={() => {
                    setStatusValue(checkInData.checkinStatus || '');
                    setSubStatusValue(checkInData.checkinSubStatus || '');
                    setStatusMessageValue(checkInData.statusMessage || '');
                    setEditingStatus(true);
                  }}
                  className="p-1.5 rounded-lg bg-white text-gray-600 hover:text-blue-600 transition-all shadow-sm border border-gray-100"
                  title="Edit Check-in Status"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
            >
              {editingStatus ? (
                <div className="space-y-2">
                  <CheckInStatusPresets
                    onSelect={(preset) => {
                      setStatusValue(preset.status);
                      setSubStatusValue(preset.subStatus);
                      setStatusMessageValue(preset.message);
                    }}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel id="ext-checkin-status-label">Status</InputLabel>
                    <Select
                      labelId="ext-checkin-status-label"
                      value={statusValue || ''}
                      onChange={(e) => {
                        setStatusValue(e.target.value);
                        // Keep the sub-status only if it goes with the new status.
                        setSubStatusValue(subStatusForStatus(e.target.value, subStatusValue));
                      }}
                      label="Status"
                    >
                      {statusOptions(statusValue).map((option) => (
                        <MenuItem key={option.value} value={option.value} disabled={option.invalid}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="ext-checkin-sub-status-label" shrink>Sub-status</InputLabel>
                    <Select
                      labelId="ext-checkin-sub-status-label"
                      label="Sub-status"
                      // Show "None" for an empty sub-status instead of a blank field.
                      displayEmpty
                      notched
                      value={subStatusValue || ''}
                      onChange={(e) => setSubStatusValue(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {subStatusOptions(statusValue, subStatusValue).map((option) => (
                        <MenuItem key={option.value} value={option.value} disabled={option.invalid}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Message"
                    value={statusMessageValue}
                    onChange={(e) => setStatusMessageValue(e.target.value)}
                    placeholder="Optional status message"
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="small" onClick={handleStatusUpdate} fullWidth variant="contained">
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingStatus(false);
                        setStatusValue(checkInData.checkinStatus || '');
                        setSubStatusValue(checkInData.checkinSubStatus || '');
                        setStatusMessageValue(checkInData.statusMessage || '');
                      }}
                      fullWidth
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <SidebarInfoRow label="Status">
                    <SidebarStatusBadge status={checkInData.checkinStatus} />
                  </SidebarInfoRow>
                  {checkInData.checkinSubStatus && (
                    <SidebarInfoRow label="Sub-status">
                      <span className="text-xs font-semibold text-gray-800">
                        {subStatusLabel(checkInData.checkinSubStatus)}
                      </span>
                    </SidebarInfoRow>
                  )}
                  {hasDistinctStatusMessage && (
                    <div className="pt-1">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block mb-1">
                        Message
                      </span>
                      <p className="text-xs text-gray-600 leading-relaxed bg-gray-50/50 rounded-lg p-2">
                        {statusMessageText}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </SidebarSection>

            <SidebarSection title="Booking Info" icon={FileText} accentClass="bg-emerald-50">
              <div className="space-y-1">
                {editingPnr ? (
                  <div className="py-1 space-y-2">
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide font-medium block">
                      PNR
                    </span>
                    <TextField
                      size="small"
                      value={pnrValue}
                      onChange={(e) => {
                        setPnrValue(e.target.value);
                        setPnrError('');
                      }}
                      error={!!pnrError}
                      fullWidth
                      helperText={pnrError || 'PNR must be 6-14 characters'}
                    />
                    <div className="flex items-center gap-2">
                      <Button size="small" onClick={handlePnrUpdate} fullWidth variant="contained">
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingPnr(false);
                          setPnrValue(checkInData.pnr || '');
                          setPnrError('');
                        }}
                        fullWidth
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <SidebarInfoRow label="PNR">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-xs font-mono font-semibold text-blue-600">
                        {pnrValue || 'N/A'}
                      </span>
                      <SidebarCopyButton
                        value={pnrValue}
                        label="PNR"
                        onCopy={copyToClipboard}
                      />
                      <button
                        onClick={() => {
                          setEditingPnr(true);
                          setPnrError('');
                        }}
                        className="p-1 rounded-md bg-gray-50 text-gray-400 hover:bg-emerald-50 hover:text-emerald-500 transition-all"
                        title="Edit PNR"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    </div>
                  </SidebarInfoRow>
                )}
                <SidebarInfoRow label="Booking ID">
                  <span className="text-xs font-semibold text-gray-800">
                    {checkInData.bookingId || 'N/A'}
                  </span>
                </SidebarInfoRow>
                <SidebarInfoRow label="Subscription">
                  <span className="text-xs font-mono font-semibold text-gray-800 truncate max-w-[180px] block">
                    {subscriptionId}
                  </span>
                </SidebarInfoRow>
              </div>
            </SidebarSection>

            <SidebarSection title="Traveller Info" icon={User} accentClass="bg-amber-50">
              <div className="space-y-1">
                <SidebarInfoRow label="Last Name">
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="text-xs font-semibold text-gray-800 truncate max-w-[190px] block">
                      {travellerLastName || 'N/A'}
                    </span>
                    <SidebarCopyButton
                      value={travellerLastName}
                      label="Traveller Last Name"
                      onCopy={copyToClipboard}
                    />
                  </div>
                </SidebarInfoRow>
                <SidebarInfoRow label="Mobile">
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="text-xs font-semibold text-gray-800 truncate max-w-[190px] block">
                      {travellerPhone || 'N/A'}
                    </span>
                    <SidebarCopyButton
                      value={travellerPhone}
                      label="Mobile Number"
                      onCopy={copyToClipboard}
                    />
                  </div>
                </SidebarInfoRow>
                <SidebarInfoRow label="Email">
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="text-xs font-semibold text-gray-800 truncate max-w-[190px] block">
                      {travellerEmail || 'N/A'}
                    </span>
                    <SidebarCopyButton
                      value={travellerEmail}
                      label="Email"
                      onCopy={copyToClipboard}
                    />
                  </div>
                </SidebarInfoRow>
              </div>
            </SidebarSection>

            <SidebarSection title="Partner" icon={Building2} accentClass="bg-slate-50">
              <div className="space-y-1">
                <SidebarInfoRow label="Name">
                  <span className="text-xs font-semibold text-gray-800">
                    {checkInData.partnerName || 'N/A'}
                  </span>
                </SidebarInfoRow>
              </div>
            </SidebarSection>
          </div>
        </div>

        {/* Passenger Dialog */}
        <Dialog open={showPassengerDialog} onClose={() => setShowPassengerDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingPassenger !== null ? 'Edit Passenger' : 'Add Passenger'}</DialogTitle>
          <DialogContent sx={{ pt: 2, maxHeight: '70vh', overflowY: 'auto' }}>
            {/* Basic Information */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: '600' }}>Basic Information</Typography>
            <TextField
              label="First Name"
              fullWidth
              margin="normal"
              value={passengerFormData.first_name || ''}
              onChange={(e) => {
                setPassengerFormData({ ...passengerFormData, first_name: e.target.value });
                setPassengerFormErrors({ ...passengerFormErrors, first_name: '' });
              }}
              error={!!passengerFormErrors.first_name}
              helperText={passengerFormErrors.first_name}
            />
            <TextField
              label="Last Name"
              fullWidth
              margin="normal"
              value={passengerFormData.last_name || ''}
              onChange={(e) => {
                setPassengerFormData({ ...passengerFormData, last_name: e.target.value });
                setPassengerFormErrors({ ...passengerFormErrors, last_name: '' });
              }}
              error={!!passengerFormErrors.last_name}
              helperText={passengerFormErrors.last_name}
            />
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              value={passengerFormData.email || ''}
              onChange={(e) => {
                setPassengerFormData({ ...passengerFormData, email: e.target.value });
                setPassengerFormErrors({ ...passengerFormErrors, email: '' });
              }}
              error={!!passengerFormErrors.email}
              helperText={passengerFormErrors.email || 'Format: example@domain.com'}
            />
            <TextField
              label="Phone"
              fullWidth
              margin="normal"
              value={passengerFormData.phone || ''}
              onChange={(e) => {
                setPassengerFormData({ ...passengerFormData, phone: e.target.value });
                setPassengerFormErrors({ ...passengerFormErrors, phone: '' });
              }}
              error={!!passengerFormErrors.phone}
              helperText={passengerFormErrors.phone || 'Format: +[country code][number] (10-15 digits)'}
            />
            <TextField
              label="Date of Birth"
              fullWidth
              margin="normal"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={passengerFormData.date_of_birth || ''}
              onChange={(e) => {
                setPassengerFormData({ ...passengerFormData, date_of_birth: e.target.value });
                setPassengerFormErrors({ ...passengerFormErrors, date_of_birth: '' });
              }}
              error={!!passengerFormErrors.date_of_birth}
              helperText={passengerFormErrors.date_of_birth || 'Future dates are allowed'}
            />

            {/* Passport Information */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: '600' }}>Passport Information (Optional)</Typography>
            <TextField
              label="Passport Number"
              fullWidth
              margin="normal"
              value={passengerFormData.passport?.number || ''}
              onChange={(e) => setPassengerFormData({
                ...passengerFormData,
                passport: { ...passengerFormData.passport, number: e.target.value }
              })}
            />
            <TextField
              label="Passport Expiry Date"
              fullWidth
              margin="normal"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={passengerFormData.passport?.expiry_date || ''}
              onChange={(e) => setPassengerFormData({
                ...passengerFormData,
                passport: { ...passengerFormData.passport, expiry_date: e.target.value }
              })}
            />
            <Autocomplete
              options={countryOptions}
              value={countryOptions.find(opt => opt.value === passengerFormData.passport?.nationality) || null}
              onChange={(event, newValue) => {
                setPassengerFormData({
                  ...passengerFormData,
                  passport: { ...passengerFormData.passport, nationality: newValue?.value || '' }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nationality"
                  margin="normal"
                  placeholder="Search country..."
                />
              )}
              fullWidth
              size="small"
            />

            {/* Seat Preference */}
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: '600' }}>Seat Preference (Optional)</Typography>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Row Preference</InputLabel>
              <Select
                value={passengerFormData.seat_preference?.row_preference || ''}
                onChange={(e) => setPassengerFormData({
                  ...passengerFormData,
                  seat_preference: { ...passengerFormData.seat_preference, row_preference: e.target.value }
                })}
                label="Row Preference"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="front">Front</MenuItem>
                <MenuItem value="middle">Middle</MenuItem>
                <MenuItem value="rear">Rear</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>Column Preference</InputLabel>
              <Select
                value={passengerFormData.seat_preference?.column_preference || ''}
                onChange={(e) => setPassengerFormData({
                  ...passengerFormData,
                  seat_preference: { ...passengerFormData.seat_preference, column_preference: e.target.value }
                })}
                label="Column Preference"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="window">Window</MenuItem>
                <MenuItem value="middle">Middle</MenuItem>
                <MenuItem value="aisle">Aisle</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setShowPassengerDialog(false); setPassengerFormErrors({}); }}>Cancel</Button>
            <Button onClick={handleSavePassenger} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this passenger?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleDeletePassenger} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        {showToast && (
          <div style={{
            position: 'fixed',
            bottom: '16px',
            right: '16px',
            padding: '12px 16px',
            backgroundColor: toastType === 'error' ? '#ef4444' : '#10b981',
            color: 'white',
            borderRadius: '6px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000
          }}>
            {toastMessage}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ExtCheckInDetailsPage;
